<template>
  <div class="schedule-message-fields">
    <validation-provider #default="{ errors }" name="Canal" rules="required" vid="scheduled_message_bot">
      <small class="v-err text-danger">{{ errors[0] }}</small>

      <div class="field-label">
        <label v-show="bot" for="scheduled_message_bot">Canal</label>

        <v-select
          @input="handleBotChange()"
          :options="bots"
          :loading="hasBotsRequest"
          :clearable="false"
          id="scheduled_message_bot"
          label="name"
          class="labeled-vselect"
          placeholder="Canal"
          v-model="bot"
        />
      </div>
    </validation-provider>

    <div class="date-time-content">
      <validation-provider
        #default="{ errors }"
        name="Data do envio"
        rules="required"
        vid="scheduled_message_date"
      >
        <small class="text-danger">{{ errors[0] }}</small>

        <div class="field-label">
          <label v-show="date" for="scheduled_message_date">Data do envio</label>

          <date-picker
            @input="handleScheduleChange()"
            :disabled-date="isDateBeforeToday"
            id="scheduled_message_date"
            class="task-date"
            placeholder="Data do envio"
            format="DD/MM/YYYY"
            value-type="YYYY-MM-DD"
            v-model="date"
          />
        </div>
      </validation-provider>

      <validation-provider
        #default="{ errors }"
        name="Horário do envio"
        rules="required"
        vid="scheduled_message_time"
      >
        <small class="text-danger">{{ errors[0] }}</small>

        <div class="field-label">
          <label v-show="time" for="scheduled_message_time">Horário do envio</label>

          <date-picker
            @input="handleScheduleChange()"
            id="scheduled_message_time"
            class="task-date"
            type="time"
            placeholder="Horário do envio"
            format="HH:mm"
            value-type="HH:mm"
            v-model="time"
          />
        </div>
      </validation-provider>
    </div>

    <div v-if="bot && !hasDialogsRequest" :class="canUseFreeText ? 'window-hint' : 'window-warning'">
      <feather-icon :icon="canUseFreeText ? 'ClockIcon' : 'AlertTriangleIcon'" size="16" />

      <div>{{ windowMessage }}</div>
    </div>

    <div v-if="canUseFreeText" class="check-holder">
      <b-form-checkbox @input="handleTypeChange()" id="scheduled_message_type" switch v-model="useTemplate" />

      <div class="check-label">Modo: {{ useTemplate ? "Template" : "Texto livre" }}</div>
    </div>

    <validation-provider
      v-if="useTemplate"
      #default="{ errors }"
      name="Template"
      rules="required"
      vid="scheduled_message_template"
    >
      <small class="v-err text-danger">{{ errors[0] }}</small>

      <div class="field-label">
        <label v-show="templatePayload" for="scheduled_message_template">Template</label>

        <v-select
          @input="handleTemplateSelection($event)"
          :options="templates"
          :loading="hasTemplateRequest || hasTemplateDetailRequest"
          :reduce="(option) => option.name"
          :disabled="!bot"
          :clearable="false"
          id="scheduled_message_template"
          label="name"
          class="labeled-vselect"
          placeholder="Selecione o template"
          v-model="templateName"
        />
      </div>
    </validation-provider>

    <validation-provider
      v-else
      #default="{ errors }"
      name="Mensagem"
      rules="required"
      vid="scheduled_message_message"
    >
      <small class="v-err text-danger">{{ errors[0] }}</small>

      <div class="field-label">
        <label v-show="message" for="scheduled_message_message">Mensagem</label>

        <textarea
          @input="handleScheduleChange()"
          id="scheduled_message_message"
          class="observation"
          maxlength="1000"
          rows="4"
          placeholder="💬 Mensagem que será enviada"
          v-model="message"
        />
      </div>
    </validation-provider>

    <div v-if="useTemplate && templatePayload" class="template-preview">
      {{ templatePayload.message }}
    </div>

    <b-modal id="bv-schedule_message_template_form" class="modal-dialog" hide-footer hide-header>
      <temp-form
        v-if="selectedTemplate"
        @send-template="handleTemplateFilled($event)"
        @closed="handleTemplateFormClosed()"
        :templateData="selectedTemplate"
        :opportunity-data="opportunity"
        confirm-label="Confirmar"
      />
    </b-modal>
  </div>
</template>

<script>
import { BFormCheckbox } from "bootstrap-vue";
import DatePicker from "vue2-datepicker";
import moment from "moment";
import vSelect from "vue-select";
import { ValidationProvider } from "vee-validate";
import { required } from "@validations";
import "vue2-datepicker/index.css";

import TemplateForm from "@/views/forms/TemplateForm.vue";
import { buildScheduledTemplate } from "@/services/templateService";

export default {
  name: "ScheduleMessageFields",

  components: {
    BFormCheckbox,
    DatePicker,
    vSelect,
    ValidationProvider,

    "temp-form": TemplateForm,
  },

  props: {
    opportunity: {
      type: Object,
      default: null,
    },

    value: {
      type: Object,
      default: null,
    },

    defaultScheduledAt: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      required,

      bots: [],
      templates: [],
      dialogs: [],

      bot: null,
      date: null,
      time: null,
      message: null,
      useTemplate: false,
      templateName: null,
      selectedTemplate: null,
      templatePayload: null,

      hasChosenType: false,

      hasBotsRequest: false,
      hasDialogsRequest: false,
      hasTemplateRequest: false,
      hasTemplateDetailRequest: false,
    };
  },

  computed: {
    user() {
      return this.$store.getters["app/getUserData"];
    },

    scheduledAt() {
      if (!this.date || !this.time) return null;

      return `${this.date} ${this.time}:00`;
    },

    openDialog() {
      if (!this.bot) return null;

      return this.dialogs.find((dialog) => !dialog.closed_at && dialog.bot_id == this.bot.id) || null;
    },

    windowExpiresAt() {
      return this.openDialog?.expires_at || null;
    },

    canUseFreeText() {
      if (!this.windowExpiresAt || !this.scheduledAt) return false;

      return moment(this.scheduledAt).isBefore(moment(this.windowExpiresAt));
    },

    windowMessage() {
      if (this.canUseFreeText) {
        return `Janela de atendimento aberta até ${this.formatDateTime(this.windowExpiresAt)} — é possível enviar texto livre.`;
      }

      if (this.windowExpiresAt) {
        return `A janela deste canal encerra em ${this.formatDateTime(this.windowExpiresAt)} e o envio está agendado para depois disso — será necessário usar um template.`;
      }

      return "Não há janela de atendimento aberta neste canal — o envio será feito por template.";
    },
  },

  watch: {
    opportunity: {
      handler() {
        this.fetchDialogs();
      },
    },

    canUseFreeText: {
      handler() {
        this.applyWindowRule();

        this.emitPayload();
      },
    },
  },

  created() {
    this.setInitialValues();

    this.fetchBots();

    this.fetchDialogs();

    if (this.useTemplate && this.bot) {
      this.fetchTemplates();
    }
  },

  methods: {
    setInitialValues() {
      const scheduledAt = this.value?.scheduled_at || this.defaultScheduledAt;

      if (scheduledAt) {
        this.date = moment(scheduledAt).format("YYYY-MM-DD");
        this.time = moment(scheduledAt).format("HH:mm");
      }

      if (!this.value) return;

      this.hasChosenType = true;
      this.message = this.value.message || null;
      this.useTemplate = Boolean(this.value.template);
      this.templateName = this.value.template?.name || null;

      if (this.value.template) {
        this.templatePayload = {
          message: this.value.message,
          template: this.value.template,
        };
      }

      if (this.value.bot_id) {
        this.bot = { id: this.value.bot_id, name: this.value.bot_name };
      }
    },

    formatDateTime(value) {
      return moment(value).format("DD/MM/YYYY [às] HH:mm");
    },

    isDateBeforeToday(date) {
      return moment(date).isBefore(moment().startOf("day"));
    },

    isBotAvailable(bot) {
      const isConnected = bot.is_whatsapp_qr_code_connected || bot.is_whatsapp_business_connected;

      return Boolean(bot.active && isConnected);
    },

    applyWindowRule() {
      if (this.hasDialogsRequest || !this.bot) return;

      if (!this.canUseFreeText && !this.useTemplate) {
        this.useTemplate = true;

        if (!this.templates.length) {
          this.fetchTemplates();
        }

        return;
      }

      if (this.canUseFreeText && this.useTemplate && !this.hasChosenType) {
        this.useTemplate = false;
      }
    },

    emitPayload() {
      const hasContent = this.useTemplate ? Boolean(this.templatePayload) : Boolean(this.message);

      if (!this.bot || !this.scheduledAt || !hasContent) {
        this.$emit("input", null);

        return;
      }

      const payload = {
        user_id: this.value?.user_id || this.user?.id,
        bot_id: this.bot.id,
        bot_name: this.bot.name,
        scheduled_at: this.scheduledAt,
        message: this.useTemplate ? this.templatePayload?.message : this.message,
        template: this.useTemplate ? this.templatePayload?.template : null,
      };

      this.$emit("input", payload);
    },

    hideTemplateForm() {
      this.$bvModal.hide("bv-schedule_message_template_form");
    },

    handleScheduleChange() {
      this.applyWindowRule();

      this.emitPayload();
    },

    handleBotChange() {
      this.templates = [];
      this.templateName = null;
      this.selectedTemplate = null;
      this.templatePayload = null;

      this.applyWindowRule();

      if (this.useTemplate && !this.templates.length) {
        this.fetchTemplates();
      }

      this.emitPayload();
    },

    handleTypeChange() {
      this.hasChosenType = true;
      this.templateName = null;
      this.selectedTemplate = null;
      this.templatePayload = null;

      if (this.useTemplate && !this.templates.length) {
        this.fetchTemplates();
      }

      this.emitPayload();
    },

    handleTemplateSelection(templateName) {
      if (!templateName) return;

      this.templatePayload = null;

      this.fetchTemplateDetail(templateName);
    },

    handleTemplateFilled(filledTemplate) {
      this.templatePayload = buildScheduledTemplate(filledTemplate);

      this.hideTemplateForm();

      this.emitPayload();
    },

    handleTemplateFormClosed() {
      this.hideTemplateForm();

      if (this.templatePayload) return;

      this.templateName = null;
      this.selectedTemplate = null;

      this.emitPayload();
    },

    fetchBots() {
      this.hasBotsRequest = true;

      const params = [{ query: "workflow_id", param: this.$service.getWorkflowId() }];

      this.$store
        .dispatch("omnichannel/listBots", this.$service.formatQueryParam(params))
        .then((response) => {
          this.bots = (response.data || []).filter((bot) => this.isBotAvailable(bot));

          if (this.bot) {
            this.bot = this.bots.find((bot) => bot.id == this.bot.id) || this.bot;

            return;
          }

          if (this.bots.length === 1) {
            this.bot = this.bots[0];

            this.handleBotChange();
          }
        })
        .finally(() => {
          this.hasBotsRequest = false;
        });
    },

    fetchDialogs() {
      this.dialogs = [];

      if (!this.opportunity?.id) return;

      this.hasDialogsRequest = true;

      this.$store
        .dispatch("opportunityStore/fetchOne", this.opportunity.id)
        .then((response) => {
          this.dialogs = response.data?.dialogs || [];
        })
        .finally(() => {
          this.hasDialogsRequest = false;

          this.applyWindowRule();

          this.emitPayload();
        });
    },

    fetchTemplates() {
      if (!this.bot || this.hasTemplateRequest) return;

      this.hasTemplateRequest = true;

      this.$store
        .dispatch("omnichannel/listTemplatesByBot", this.bot.id)
        .then((response) => {
          this.templates = response.data;
        })
        .finally(() => {
          this.hasTemplateRequest = false;
        });
    },

    fetchTemplateDetail(templateName) {
      this.hasTemplateDetailRequest = true;

      const params = {
        template_name: templateName,
        bot_id: this.bot.id,
      };

      this.$store
        .dispatch("omnichannel/getOneTemplateByBot", params)
        .then((response) => {
          this.selectedTemplate = {
            ...response.data,
            bot: this.bot,
          };

          this.$nextTick(() => {
            this.$bvModal.show("bv-schedule_message_template_form");
          });
        })
        .finally(() => {
          this.hasTemplateDetailRequest = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule-message-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  .field-label {
    position: relative;
    width: 100%;

    label {
      position: absolute;
      top: -11px;
      left: 15px;
      z-index: 100;
      padding: 3px;
      font-size: 12px;
      font-weight: 400;
      border-radius: 5px;
      color: #b1b1b1;
      background: #ffffff;
    }

    :deep(.labeled-vselect) {
      .vs__dropdown-toggle {
        padding: 5px 5px 9px 5px;
      }

      .vs__selected {
        color: #6e6b7b;
      }
    }
  }

  .date-time-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    width: 100%;
    margin: 10px 0;
    gap: 10px;

    .task-date {
      width: 100%;
    }

    :deep(.task-date) {
      .mx-input {
        height: 42px;
        border: 1px solid #c6c6c6;
        border-radius: 5px;
        color: #6e6b7b;
        box-shadow: none;
      }
    }
  }

  .window-hint,
  .window-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 10px;
    font-size: 12px;
    line-height: 16px;
    border-radius: 6px;
  }

  .window-hint {
    color: #046af3;
    background: #eaf2ff;
  }

  .window-warning {
    color: #b26a00;
    background: #fff4e5;
  }

  .check-holder {
    display: flex;
    align-items: center;
    padding: 10px;
    gap: 5px;
    border-radius: 5px;
    background: #f5f5f5;
  }

  .observation {
    width: 100%;
    padding: 10px;
    border-radius: 5px;
    border: 1px solid #c6c6c6;
    outline: none;
    color: #6e6b7b;
    resize: none;
  }

  .template-preview {
    padding: 10px;
    font-size: 12px;
    white-space: pre-wrap;
    color: #14223b;
    background: #f4f6f9;
    border-radius: 6px;
  }
}
</style>
